import type { Control, FieldErrors, FieldValues } from "react-hook-form";

import FormSection from "~/components/formElements/FormSection";
import PaymentTargetDaysInput from "~/components/formElements/PaymentTargetInput";
import type {
	PaymentTargetDaysInputFormData,
} from "~/components/formElements/PaymentTargetInput/paymentTargetDaysInputTypes.ts";
import RadioGroup from "~/components/formElements/RadioGroup";
import Select from "~/components/formElements/Select";
import Switch from "~/components/formElements/Switch";
import {
	FIXED_PRICE_OPTIONS,
	MONTHLY_BILLING_OPTIONS,
	SKILL_BASED_OPTIONS,
	TRAVEL_EXPENSES_INCLUDED_OPTIONS,
} from "~/constants/form.ts";
import { EMPTY_VALUE_PLACEHOLDER } from "~/constants/textConstants.ts";
import BillingSettingsProjectValue
	from "~/modules/billing/components/formSections/components/BillingSettingsProjectValue.tsx";
import type { BillingSettingsDivergingFields } from "~/modules/billing/utils/billingSettingsDisplayUtils.ts";
import type { ParsedBillingSettings } from "~/modules/billing/utils/billingSettingsFormUtils.ts";
import type { FormInputOption } from "~/types/form.ts";

type Props<T extends FieldValues> = {
	control: Control<T>;
	currencySelectOptions: FormInputOption[];
	errors: FieldErrors<T>;
	fieldsDivergingFromProject?: BillingSettingsDivergingFields;
	showCurrencySelect: boolean;
	showCustomPaymentTargetInput: boolean;
	projectBillingSettings?: ParsedBillingSettings;
};

interface BillingAccountingFormData {
	currencyId: string;
	isBilledMonthly: boolean;
	isFixedPrice: boolean;
	isForeignCountry: boolean;
	isSkillBased: boolean;
	travelExpensesIncluded: boolean;
}

const BillingAccountingFormSection = <T extends BillingAccountingFormData & PaymentTargetDaysInputFormData>({
	control,
	currencySelectOptions,
	errors,
	fieldsDivergingFromProject,
	showCurrencySelect,
	showCustomPaymentTargetInput,
}: Props<T>) => {
	return (
		<FormSection title="Abrechnung">
			<div className="grid grid-cols-6 gap-x-6 gap-y-4">
				<div className="col-span-6">
					<RadioGroup
						control={control}
						label="Abrechnungsart"
						name="isFixedPrice"
						options={FIXED_PRICE_OPTIONS}
						numCols={2}
					/>
					{fieldsDivergingFromProject?.isFixedPrice.isDifferent &&
						<BillingSettingsProjectValue value={fieldsDivergingFromProject?.isFixedPrice.displayValue} />}
				</div>

				<div className="col-span-6">
					<RadioGroup
						control={control}
						label="Abrechnung Rollen"
						name="isSkillBased"
						options={SKILL_BASED_OPTIONS}
						numCols={2}
					/>
					{fieldsDivergingFromProject?.isSkillBased.isDifferent &&
						<BillingSettingsProjectValue value={fieldsDivergingFromProject?.isSkillBased.displayValue} />}
				</div>

				<div className="col-span-6">
					<RadioGroup
						control={control}
						label="Reisekosten"
						name="travelExpensesIncluded"
						options={TRAVEL_EXPENSES_INCLUDED_OPTIONS}
						numCols={2}
					/>
					{fieldsDivergingFromProject?.travelExpensesIncluded.isDifferent &&
						<BillingSettingsProjectValue value={fieldsDivergingFromProject?.travelExpensesIncluded.displayValue} />}
				</div>
				<div className="col-span-6">
					<RadioGroup
						control={control}
						label="Abrechnungsintervall"
						name="isBilledMonthly"
						options={MONTHLY_BILLING_OPTIONS}
						numCols={2}
					/>
					{fieldsDivergingFromProject?.isBilledMonthly.isDifferent &&
						<BillingSettingsProjectValue value={fieldsDivergingFromProject?.isBilledMonthly.displayValue} />}
				</div>

				<PaymentTargetDaysInput
					className="col-span-6"
					control={control}
					showCustomPaymentTargetInput={showCustomPaymentTargetInput}
				/>
				{fieldsDivergingFromProject?.paymentTargetDays.isDifferent &&
					<BillingSettingsProjectValue value={fieldsDivergingFromProject?.paymentTargetDays.displayValue} />}
				<div className="col-span-6">
					<Switch label="Leistung / Rechnung Ausland"
							name="isForeignCountry"
							control={control} />
				</div>

				{showCurrencySelect && (
					<div className="col-span-6">
						<Select
							control={control}
							label="Währung"
							name="currencyId"
							optionsData={currencySelectOptions}
							error={errors.currencyId?.message as string}
						/>
						{fieldsDivergingFromProject?.currencyDisplayName.isDifferent &&
							<BillingSettingsProjectValue value={fieldsDivergingFromProject?.currencyDisplayName.displayValue || EMPTY_VALUE_PLACEHOLDER} />}
					</div>
				)}
			</div>
		</FormSection>
	);
};

export default BillingAccountingFormSection;